import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, SectionTitle, Note, Image, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider, Separator, Result, CorrectionBox } from '../../../../components/course/CourseComponents';
import screen1 from '../repere2.jpg';

const Chapitre6Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Ajout d'effets sonores et du score
        </p>
      </InfoBox>

      <Separator type="text" text="Ajout du score" color="gray-darker" size="md" />
      <SectionTitle>🏆 1/ Ajout du score</SectionTitle>

      <p>Le score augmente à chaque fois que le serpent mange une pomme.</p>

      <p>On commence par créer une variable globale <InlineCode>score</InlineCode> :</p>

      <CodeBlock language="javascript">
{`let score = 0;`}
      </CodeBlock>

      <p>Ensuite, quand le serpent mange une pomme, on incrémente <InlineCode>score</InlineCode> de 1 :</p>

      <CodeBlock language="javascript">
{`// Collision nourriture
let distanceNourriture = dist(posX, posY, nourritureX, nourritureY);
if (distanceNourriture < rayon * 2) {
  longueur = longueur + nourriture;
  score += 1;
  placerNourriture();
}`}
      </CodeBlock>

      <Note type="tip" title="💡 TIPS : score += 1">
        <p>Cette ligne permet d'ajouter 1 au score. On peut aussi écrire <InlineCode>score = score + 1</InlineCode>, ou encore <InlineCode>score++</InlineCode>.</p>
      </Note>

      <p>Enfin, on affiche le score à la fin de la fonction <InlineCode>draw</InlineCode> :</p>

      <CodeBlock language="javascript">
{`// Score
textAlign(CENTER, TOP); // Texte en haut au milieu
textStyle(BOLD);        // En gras
textSize(64);           // Taille 64
fill(255);              // En blanc
text(score, width / 2, 10); // Score au milieu de l'écran`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : affichage du score">
        <p>- Crée une variable globale <InlineCode>score</InlineCode> initialisée à 0 :</p>

        <CodeBlock language="javascript">
{`let score = 0;`}
        </CodeBlock>

        <p>- Dans la fonction <InlineCode>draw</InlineCode>, dans la condition <InlineCode>if (distanceNourriture &lt; rayon * 2)</InlineCode>, incrémente le score de 1 :</p>

        <CodeBlock language="javascript">
{`score += 1;`}
        </CodeBlock>

        <p>- À la fin de la fonction <InlineCode>draw</InlineCode>, affiche le score :</p>

        <CodeBlock language="javascript">
{`// Score
textAlign(CENTER, TOP);
textStyle(BOLD);
textSize(64);
fill(255);
text(score, width / 2, 10);`}
        </CodeBlock>

        <br/>
        <Result>Le score augmente à chaque fois que le serpent mange une pomme.</Result>
      </Note>

      <Separator type="text" text="Ajout des effets sonores" color="gray-darker" size="md" />
      <SectionTitle>🔊 2/ Ajout des effets sonores</SectionTitle>

      <p>On va utiliser les 2 sons qu'on a chargés dans le premier chapitre : <InlineCode>sonMort</InlineCode> et <InlineCode>sonCroque</InlineCode>.</p>

      <p><strong>Son quand on mange une pomme :</strong></p>
      <p>Ajoute <InlineCode>sonCroque.play();</InlineCode> à l'endroit où on mange une pomme. C'est au même endroit où on augmente le score.</p>

      <p><strong>Son de mort :</strong></p>
      <p>Ajoute <InlineCode>sonMort.play();</InlineCode> dans la fonction <InlineCode>gameOver</InlineCode>.</p>

      <Note type="exercice" title="🧪 À toi de jouer : ajout des sons">
        <p>- Dans la condition <InlineCode>if (distanceNourriture &lt; rayon * 2)</InlineCode>, ajoute la lecture du son :</p>

        <CodeBlock language="javascript">
{`sonCroque.play();`}
        </CodeBlock>

        <p>- Dans la fonction <InlineCode>gameOver</InlineCode>, ajoute :</p>

        <CodeBlock language="javascript">
{`sonMort.play();`}
        </CodeBlock>

        <br/>
        <Result>Un son est émis quand on mange une pomme et quand on meurt.</Result>
      </Note>

      <Separator type="text" text="Équilibrage du jeu" color="gray-darker" size="md" />
      <SectionTitle>⚖️ 3/ Équilibrage du jeu</SectionTitle>

      <p>Voici les variables que tu peux ajuster pour rendre le jeu plus ou moins difficile :</p>

      <CodeBlock language="javascript">
{`let vitesse = 270;`}
      </CodeBlock>

      Détermine la vitesse du serpent. Essaie 250 pour un gameplay plus facile, 300 pour un jeu plus rapide.

      <CodeBlock language="javascript">
{`let longueur = 16;`}
      </CodeBlock>

      Détermine la longueur initiale du serpent. Si tu trouves le début trop lent, augmente à 50 par exemple.

      <CodeBlock language="javascript">
{`let nourriture = 12;`}
      </CodeBlock>

      Détermine la taille ajoutée au serpent à chaque pomme mangée. Si tu trouves le jeu trop long, augmente cette valeur (ex : 50).

      <Separator type="text" text="Code final" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code Final</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 4 : Snake
let imageFond;
let imageTete1;
let imageTete2;
let imageTeteMort;
let imageCorps;
let imagePomme;
let sonMort;
let sonCroque

let vitesse = 270; // vitesse du snake en pixels par seconde
let longueur = 16; // longueur initiale du snake
let nourriture = 12; // longueur supplémentaire à chaque pomme

let posX, posY;
let dx = 1, dy = 0;
let corps = [];
let nourritureX, nourritureY;
let rayon = 20;
let score = 0;

//--------------------------------- PRELOAD ------------------------------------------
function preload(){
  imageFond = loadImage('fond.png')
  imageTete1 = loadImage('snake-tete1.png');
  imageTete2 = loadImage('snake-tete2.png');
  imageTeteMort = loadImage('snake-tete-mort.png');
  imageCorps = loadImage('snake-corps.png');
  imagePomme = loadImage('pomme.png');
  sonMort = loadSound('mort.wav')
  sonCroque = loadSound('croque.wav')
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(500, 500);
  posX = 40;
  posY = height / 2;
  placerNourriture();
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  imageMode(CORNER);
  background(imageFond);
  imageMode(CENTER);

  // Déplacement
  let dt = deltaTime / 1000;
  posX = posX + dx * vitesse * dt;
  posY = posY + dy * vitesse * dt;
  corps.push({ x: posX, y: posY });

  // Taille limite
  while (corps.length > longueur) {
    corps.shift();
  }

  // Affichage corps
  for (let i = 0; i < corps.length; i = i + 4) {
    let p = corps[i];
    image(imageCorps, p.x, p.y);
  }

  // Affichage et animation de la tête
  let imageTete;
  if (frameCount % 100 > 10)  imageTete = imageTete1;
  else imageTete = imageTete2;
  image(imageTete, posX, posY);

  // Affichage nourriture
  image(imagePomme, nourritureX, nourritureY);

  // Collision nourriture
  let distanceNourriture = dist(posX, posY, nourritureX, nourritureY);
  if (distanceNourriture < rayon * 2) {
    longueur = longueur + nourriture;
    score += 1;
    sonCroque.play();
    placerNourriture();
  }

  // Collision mur
  if (posX - rayon < 0 || posX + rayon > width || posY-rayon < 0 || posY+rayon > height) {
    gameOver("AIE, LE MUR !");
  }

  // Collision soi-même
  for (let i = 0; i < corps.length - 15; i += 8) {
    if (dist(posX, posY, corps[i].x, corps[i].y) < rayon*2) {
      gameOver("TU T'ES MORDU !");
    }
  }

  // Score
  textAlign(CENTER, TOP);
  textStyle(BOLD);
  textSize(64);
  fill(255);
  text(score, width / 2, 10);
}

//--------------------------------- CLAVIER ------------------------------------------
function keyPressed() {
  if (keyCode === UP_ARROW && dy !== 1) { dx = 0; dy = -1; }
  else if (keyCode === DOWN_ARROW && dy !== -1) { dx = 0; dy = 1; }
  else if (keyCode === LEFT_ARROW && dx !== 1) { dx = -1; dy = 0; }
  else if (keyCode === RIGHT_ARROW && dx !== -1) { dx = 1; dy = 0; }
}

//--------------------------------- NOURRITURE ------------------------------------------
function placerNourriture() {
  let essais = 0;
  while (essais < 100) {
    let x = random(rayon, width - rayon);
    let y = random(rayon, height - rayon);
    let positionValide = true;
    for (let i = 0; i < corps.length; i += 8) {
      if (dist(x, y, corps[i].x, corps[i].y) < rayon * 2) {
        positionValide = false;
        break;
      }
    }
    if (positionValide) {
      nourritureX = x;
      nourritureY = y;
      return;
    }
    essais++;
  }

  // Si on dépasse 100 essais, on place quand même la nourriture quelque part
  nourritureX = random(rayon, width - rayon);
  nourritureY = random(rayon, height - rayon);
}

//--------------------------------- ECRAN FIN ------------------------------------------
function gameOver(msg) {
  noLoop();
  sonMort.play();
  image(imageTeteMort, posX, posY);
  fill(255);
  textAlign(CENTER, TOP);
  textStyle(BOLD);
  textSize(32);
  text(msg, width / 2, 70);
}`}
      </CodeBlock>


      <p><strong>🐍 Bravo ! Tu as terminé le jeu Snake ! 🎉</strong></p>

      <p>Félicitations pour ton super travail !
      <br/>Tu viens de créer un jeu complet, fluide et fun, entièrement codé par toi, avec des images, des sons, un score, des collisions et des règles.</p>

      <p><strong>Voici tout ce que tu as appris dans ce projet :</strong></p>

      <ul className=" list-inside space-y-2 ml-4">
        <li>✅ Charger et afficher des images.</li>
        <li>✅ Utiliser des sons pour rendre l'expérience plus immersive.</li>
        <li>✅ Gérer le mouvement fluide d'un serpent en utilisant des listes de positions.</li>
        <li>✅ Faire grandir dynamiquement un objet dans le jeu.</li>
        <li>✅ Placer des objets de manière aléatoire et intelligente (en évitant le corps du serpent).</li>
        <li>✅ Détecter des collisions avec les murs et avec soi-même.</li>
        <li>✅ Créer un écran de fin et afficher un score en temps réel.</li>
        <li>✅ Maîtriser des notions importantes comme les boucles, les fonctions, les conditions, la gestion du clavier, et la physique de déplacement.</li>
      </ul>

      
        <p>Ce que tu viens de coder ici, c'est bien plus qu'un jeu rétro : c'est un vrai exercice de logique et de créativité.
        <br/>N'hésite pas à revoir les différents chapitres si tu as un doute, ou à venir sur Discord pour poser des questions si tu veux aller plus loin.</p>
  

      <p><strong>Encore bravo pour tout ton parcours dans ce projet Snake ! 👏</strong></p>
    </>
  );
};

export default Chapitre6Content; 

